import React, { useState, useEffect, useRef } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AuthImage — loads an auth-protected image via an authenticated fetch and
 * renders it from a blob URL. Handles loading + error states cleanly and
 * revokes the blob URL on unmount to prevent memory leaks.
 *
 * props:
 *   path     — the image path returned by the API, e.g. "/api/attendance/photo/..."
 *   alt      — alt text
 *   className— applied to the <img> (and the placeholder container)
 *   onClick  — optional click handler (e.g. to open a full-size modal)
 */
const AuthImage = ({ path, alt = 'photo', className = '', onClick }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setSrc(null);

    const load = async () => {
      if (!path) {
        setError(true);
        setLoading(false);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const url = path.startsWith('http') ? path : `${API_URL}${path}`;
        const resp = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error(`status ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objUrl;
        setSrc(objUrl);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        console.error('AuthImage load failed:', e);
        setError(true);
        setLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [path]);

  if (loading) {
    return (
      <div
        data-testid="auth-image-loading"
        className={`flex items-center justify-center bg-gray-100 text-gray-400 ${className}`}
      >
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error || !src) {
    return (
      <div
        data-testid="auth-image-error"
        className={`flex flex-col items-center justify-center bg-gray-100 text-gray-400 text-[10px] gap-1 ${className}`}
      >
        <ImageOff className="h-5 w-5" />
        <span>Photo unavailable</span>
      </div>
    );
  }

  return (
    <img
      data-testid="auth-image"
      src={src}
      alt={alt}
      className={className}
      onClick={onClick}
      loading="lazy"
    />
  );
};

export default AuthImage;
